import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { api, downloadText, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, NativeSelect, Spinner } from "@/components/Brand";
import { Confirm } from "@/components/Confirm";
import { GoldBtn, PageTitle } from "@/pages/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const blank = (role) => ({ name: "", email: "", phone: "", password: "", role, active: true });

const UserDialog = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState(initial);
  useEffect(() => {
    setF(initial);
  }, [initial]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, password: f.password || undefined };
      if (f.id) await api.put(`/admin/users/${f.id}`, body);
      else await api.post("/admin/users", body);
      toast.success("Pengguna disimpan");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (!f) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit pengguna" : "Tambah pengguna"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Nama</Label><Input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label>Email</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label>No. HP</Label><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="user-phone-input" /></div>
          <div><Label>{f.id ? "Kata sandi baru (kosongkan jika tidak diubah)" : "Kata sandi"}</Label><Input type="password" required={!f.id} minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></div>
          <div><Label>Peran</Label>
            <NativeSelect value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role-select"><option value="peserta">Peserta</option><option value="admin">Admin</option></NativeSelect>
          </div>
          {f.id && <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" /><Label>Akun aktif</Label></div>}
          <GoldBtn type="submit" className="w-full justify-center py-2.5" data-testid="user-save-btn">Simpan</GoldBtn>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function UsersAdmin({ role }) {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const fileRef = useRef(null);
  const isAdminPage = role === "admin";

  const load = useCallback(() => {
    api.get("/admin/users", { params: { role, q: q || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [role, q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { ...u, active: !u.active, password: undefined });
      toast.success(u.active ? "Akun dinonaktifkan" : "Akun diaktifkan");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Pengguna dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/users/import", fd);
      toast.success(`${data.imported} peserta berhasil diimpor`);
      if (data.errors.length) toast.warning(data.errors.slice(0, 5).join("\n"), { duration: 10000 });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <PageTitle title={isAdminPage ? "Manajemen Admin & Hak Akses" : "Manajemen Peserta"}
        desc={isAdminPage ? "Admin memiliki akses penuh ke dashboard admin. Peserta hanya dapat melihat data miliknya sendiri." : "Tambah, ubah, nonaktifkan, atau impor akun peserta dari CSV/Excel."}>
        {!isAdminPage && <>
          <button onClick={() => downloadText("nama,email,password,telepon\nBudi Santoso,budi@contoh.com,rahasia123,08123456789\n", "template-peserta.csv")} className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold" data-testid="users-template-btn"><Download className="h-4 w-4" />Template CSV</button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={doImport} data-testid="users-import-input" />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-4 py-2 text-sm font-semibold text-white" data-testid="users-import-btn"><Upload className="h-4 w-4" />Impor CSV/Excel</button>
        </>}
        <GoldBtn onClick={() => setEdit(blank(role))} data-testid="users-add-btn"><Plus className="h-4 w-4" />Tambah {isAdminPage ? "Admin" : "Peserta"}</GoldBtn>
      </PageTitle>
      <div className="relative mb-4 max-w-sm"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input className="pl-9" placeholder="Cari nama atau email..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search-input" /></div>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty text="Tidak ada data." /> : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full min-w-[760px] text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Nama</th><th>Email</th><th>No. HP</th><th>Ujian selesai</th><th>Terdaftar</th><th>Aktif</th><th className="pr-4 text-right">Aksi</th></tr></thead>
            <tbody>{rows.map((u) => (
              <tr key={u.id} className="border-t" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-semibold">{u.name}</td><td>{u.email}</td><td>{u.phone || "-"}</td><td>{u.finished_count}</td><td>{fmtDate(u.created_at)}</td>
                <td><Switch checked={u.active} disabled={u.id === me.id} onCheckedChange={() => toggleActive(u)} data-testid={`user-toggle-${u.email}`} /></td>
                <td className="pr-4 text-right">
                  <button onClick={() => setEdit({ ...u, password: "" })} className="mr-2 text-slate-600 hover:text-[#0A192F]" data-testid={`user-edit-${u.email}`}><Pencil className="h-4 w-4" /></button>
                  {u.id !== me.id && <Confirm testid={`user-delete-${u.email}`} title="Hapus pengguna?" description={`Akun ${u.email} beserta seluruh hasil ujian dan pembayarannya akan dihapus permanen.`} onConfirm={() => del(u)}
                    trigger={<button className="text-red-600" data-testid={`user-delete-btn-${u.email}`}><Trash2 className="h-4 w-4" /></button>} />}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <UserDialog open={!!edit} initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
